/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : useAxios.js
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import axios from "axios";
import { jwtDecode } from "jwt-decode";
import dayjs from "dayjs";
import { startLoading, stopLoading } from "../redux/actions/spinner";
import store from "../redux/store";
const baseURL = process.env.REACT_APP_BASE_URL;
const REFRESH_TOKEN_URL = `${baseURL}/UCM/user/keepalive`;
const useAxios = () => {
  let authTokens = localStorage.getItem("token")
    ? localStorage.getItem("token")
    : null;
  let storageType = true; //true -> Local Storage
  if (authTokens === null) {
    storageType = false;
    authTokens = sessionStorage.getItem("token");
  }
  let activeRequests = 0;

  const startLoader = () => {
    activeRequests++;
    store.dispatch(startLoading());
  };

  const stopLoader = () => {
    activeRequests--;

    if (activeRequests <= 0) {
      activeRequests = 0;
      store.dispatch(stopLoading());
    }
  };
  const axiosInstance = axios.create({
    baseURL,
    headers: { Authorization: `Bearer ${authTokens}` },
  });

  axiosInstance.interceptors.request.use(
    async (req) => {
      const ignoreSpinner = req.ignoreAppLoadingSpinner === true;

      if (!ignoreSpinner) {
        startLoader();
        req._hasSpinner = true; // mark this request
      }

      const user = jwtDecode(authTokens);
      const isExpired = dayjs.unix(user.exp).diff(dayjs()) < 1;

      if (!isExpired) return req;

      const response = await axios.post(REFRESH_TOKEN_URL, {
        headers: { Authorization: `Bearer ${authTokens}` },
        token: authTokens,
      });

      if (response.status === 200) {
        authTokens = response.data?.data?.token;

        if (storageType) {
          localStorage.setItem("token", authTokens);
        } else {
          sessionStorage.setItem("token", authTokens);
        }
      }

      req.headers.Authorization = `Bearer ${authTokens}`;
      return req;
    },
    (error) => {
      return Promise.reject(error);
    },
  );


  axiosInstance.interceptors.response.use(
    (response) => {
      if (response.config._hasSpinner) {
        stopLoader();
      }
      return response;
    },
    (error) => {
      if (error.config?._hasSpinner) {
        stopLoader();
      }
      return Promise.reject(error);
    },
  );

  return axiosInstance;
};
export default useAxios;
